import Cookies from "js-cookie";
import { consumeAuditContext } from "@/lib/audit-context";

// ─── Generated Types (auto-generated from OpenAPI schema) ────────────────────
// Re-export all generated types so consumers can import from "@/lib/api"
export type {
  // Core model types
  Department,
  DepartmentChild,
  DepartmentDetail,
  DepartmentDetailRole,
  DepartmentDetailUser,
  LoginResponse,
  MeResponse,
  DashboardStats,
  OAuthClientCreate,
  OAuthClientList,
  OAuthClientUpdate,
  Permission,
  PositionTypeEnum,
  Role,
  RoleDetail,
  RoleDetailPermission,
  RoleDetailUser,
  SourceEnum,
  ThemePreferenceEnum,
  User,
  UserList,
  UserProfile,
  UserRole,
  // Paginated types
  PaginatedDepartmentList,
  PaginatedOAuthClientListList,
  PaginatedPermissionList,
  PaginatedRoleList,
  PaginatedUserListList,
} from "@/lib/api-client/types.gen";

import type {
  Department,
  DepartmentChild,
  DepartmentDetail,
  LoginResponse,
  DashboardStats,
  MeResponse,
  OAuthClientList,
  Permission,
  Role,
  RoleDetail,
  UserList,
  UserProfile,
} from "@/lib/api-client/types.gen";

// ─── Backward-Compatible Type Aliases ────────────────────────────────────────
// These map old hand-written names to the auto-generated types.
// Existing code can continue importing SSOUser, SSORole, etc. from "@/lib/api"

/** UAE Pass profile data (nested in user detail) */
export type UAEPassProfileData = {
  uuid: string;
  idn: string;
  fullname_en: string;
  fullname_ar: string;
  firstname_en: string;
  firstname_ar: string;
  lastname_en: string;
  lastname_ar: string;
  email: string;
  mobile: string;
  nationality_en: string;
  nationality_ar: string;
  gender: string;
  user_type_uaepass: string;
  profile_type: string;
  last_login_via_uaepass: string | null;
};

/** User detail/profile view (GET /api/sso/users/{id}/) */
export type SSOUser = Omit<UserProfile, "department_name" | "department_code" | "department_name_en" | "direct_manager_name"> & {
  department_name?: string | null;
  department_code?: string | null;
  department_name_en?: string | null;
  direct_manager_name?: string | null;
  national_id?: string | null;
  uae_pass_uuid?: string | null;
  uae_pass_profile?: UAEPassProfileData | null;
  created_at?: string | null;
  updated_at?: string | null;
  last_login?: string | null;
  permissions?: string[];
  must_change_password?: boolean;
  /** RBAC scope-4: full department membership list. Primary match is `department_id`. */
  departments?: Array<{
    id: number;
    code: string;
    name: string;
    is_primary: boolean;
  }>;
};

/** User role entry (from PermissionService.get_user_roles) */
export type SSOUserRole = {
  id: number;
  code: string;
  name: string;
  name_ar?: string | null;
  department_id?: number | null;
  department_code?: string | null;
  source?: "department" | "assigned";
};

/** Role list/detail (combined for backward compat).
 *
 * The generated `Role` / `RoleDetail` types still carry `system`,
 * `system_name`, and `system_client_id` from the pre-scope-2 schema.
 * Migration 0027 dropped the `Role.system` FK — those fields no longer
 * exist on the backend, so we strip them from the public UI type. */
export type SSORole = Omit<
  Role,
  "permissions" | "system" | "system_name" | "system_client_id"
> &
  Partial<Pick<RoleDetail, "permissions" | "users">> & {
    permission_ids?: number[];
    created_at?: string;
    updated_at?: string;
  };

/** Permission (codename-based) */
/** Permission (codename-based). Widens the generated type to include the
 * scope-2 `system` FK + its denormalised display fields. */
export type SSOPermission = Permission & {
  /** FK to OAuthClient (required on CREATE, read-only on list). */
  system?: number | null;
  /** Denormalised client_id of the parent OAuth system. */
  system_client_id?: string | null;
  /** Arabic display name of the parent OAuth system. */
  system_name?: string | null;
  /** English display name of the parent OAuth system. */
  system_name_en?: string | null;
};

/** Role row as returned by the permission detail endpoint. */
export interface PermissionRoleRow {
  id: number;
  name: string;
  name_ar: string | null;
  code: string;
  department_id: number | null;
  department_code: string | null;
  department_name: string | null;
  user_count: number;
}

/** User row as returned by the permission detail endpoint. */
export interface PermissionUserRow {
  id: number;
  username: string;
  full_name: string;
  email: string;
  employee_id: string | null;
  job_title: string | null;
  department_name: string | null;
  is_active: boolean;
  /** Roles through which this user holds the permission — a user may hold
   * the same permission via more than one role. */
  roles: Array<{
    id: number;
    name: string;
    name_ar: string | null;
    code: string;
    department_id: number | null;
    department_name: string | null;
  }>;
}

/** Detail response from GET /api/sso/permissions/{id}/ — superset of SSOPermission. */
export type SSOPermissionDetail = SSOPermission & {
  roles: PermissionRoleRow[];
  users: PermissionUserRow[];
};

/** Role user (in role detail) */
export type RoleUser = {
  id: number;
  username: string;
  full_name: string;
  email: string;
  employee_id: string | null;
  department_name: string | null;
  is_active: boolean;
};

/** Role permission (in role detail) */
export type RolePermission = {
  id: number;
  name: string;
  display_name: string | null;
  module: string;
  description: string | null;
};

/** Department user (in department detail) */
export type DepartmentUser = {
  id: number;
  username: string;
  full_name: string;
  email: string;
  employee_id: string | null;
  job_title: string | null;
  is_active: boolean;
};

/** Department role (in department detail) */
export type DepartmentRole = {
  id: number;
  name: string;
  name_ar: string | null;
  code: string;
  is_system_role: boolean;
};

/** Department child (in department detail, widened from generated type).
 *
 * The generated `DepartmentChild` only declares id/name/name_en/code/level/
 * is_active, but the department detail serializer returns `user_count` per
 * child so the UI can render per-child employee counts on the sub-departments
 * tab. Widened here rather than mutating `types.gen.ts`. */
export type DepartmentChildWithCount = DepartmentChild & {
  user_count?: number;
};

/** Department list/detail (combined for backward compat) */
export type SSODepartment = Department & Omit<Partial<DepartmentDetail>, "children"> & {
  /** Backward compat: Arabic name (same as `name` which is Arabic in the schema) */
  name_ar?: string;
  /** Backward compat: alias for parent_department_id */
  parent?: number | null;
  /** Backward compat: alias for parent_department_name */
  parent_name?: string;
  /** Widened children type (adds optional `user_count`). */
  children?: DepartmentChildWithCount[];
};

/** Department tree node (recursive) */
export interface SSODepartmentTreeNode extends Omit<SSODepartment, "children"> {
  children: SSODepartmentTreeNode[];
}

/** OAuth client (unified type for all operations) */
export type OAuthClient = OAuthClientList & {
  client_secret?: string;
  redirect_uris?: string[];
  cors_origins?: string[];
  secret?: string;
};

/** Accessible system for user apps dashboard */
export type AccessibleSystem = {
  id: number;
  client_id: string;
  name: string;
  name_en: string | null;
  description: string | null;
  redirect_uri: string | null;
};

/** Generic paginated response */
export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

// ─── Base URL ────────────────────────────────────────────────────────────────
const SSO_API_URL =
  process.env.NEXT_PUBLIC_SSO_API_URL || "http://localhost:8001";

// ─── Error Class ─────────────────────────────────────────────────────────────

export class ApiError extends Error {
  status: number;
  body: Record<string, unknown>;

  constructor(status: number, body: Record<string, unknown>) {
    super(`API Error ${status}`);
    this.status = status;
    this.body = body;
  }
}

// ─── Token Management ────────────────────────────────────────────────────────

let isRefreshing = false;
let refreshPromise: Promise<string | null> | null = null;

function getAccessToken(): string | undefined {
  return Cookies.get("sso_access_token");
}

function getRefreshToken(): string | undefined {
  return Cookies.get("sso_refresh_token");
}

// ─── Cached user / JWT-claim helpers ─────────────────────────────────────
//
// Login (password and UAE Pass) writes the full user payload to
// localStorage["sso_user"]. The access JWT carries `permissions`, `roles`,
// `systems`, `user_id`, `session_id`. Components that just need a stable
// is_superuser / permissions / user_id check should read these helpers
// instead of firing a fresh /api/auth/me/ — that endpoint is heavy
// (per-call permission queries) and was being called on every navigation.

/**
 * Return the user object cached at login time, or null if not present.
 * Use this for stable display data (full_name, email, is_superuser, is_staff).
 * Use `ssoApi.me()` only when you genuinely need fresh server data.
 */
export function getCachedUser(): UserProfile | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem("sso_user");
    return raw ? (JSON.parse(raw) as UserProfile) : null;
  } catch {
    return null;
  }
}

export interface JWTClaims {
  user_id?: string | number;
  session_id?: number;
  permissions?: string[];
  roles?: Array<{ id: number; code: string; name?: string; department_id?: number | null; is_active?: boolean }>;
  systems?: string[];
  exp?: number;
  iat?: number;
}

/**
 * Decode the access-token JWT claims (no signature verification).
 * Returns null if no access token or token is malformed.
 *
 * Callers needing `permissions`, `roles`, or `systems` should read them
 * here rather than calling `/api/auth/me/`.
 */
export function getJWTClaims(): JWTClaims | null {
  const token = getAccessToken();
  if (!token) return null;
  const parts = token.split(".");
  if (parts.length !== 3) return null;
  try {
    const padded = parts[1] + "=".repeat((4 - (parts[1].length % 4)) % 4);
    return JSON.parse(atob(padded.replace(/-/g, "+").replace(/_/g, "/"))) as JWTClaims;
  } catch {
    return null;
  }
}

export function setTokens(access: string, refresh: string, accessLifetime?: number) {
  // Keep cookie alive longer than JWT expiry (+1 day buffer).
  // The JWT is validated server-side; the cookie just needs to exist
  // so the client-side code can detect expiry and trigger token refresh.
  const accessExpiry = (accessLifetime ? accessLifetime / 86400 : 15 / (60 * 24)) + 1;
  const isSecure = typeof window !== "undefined" && window.location.protocol === "https:";
  Cookies.set("sso_access_token", access, {
    expires: accessExpiry,
    sameSite: "lax",
    path: "/",
    secure: isSecure,
  });
  Cookies.set("sso_refresh_token", refresh, {
    expires: 10, // 10 days (matches JWT_REFRESH_DAYS default)
    sameSite: "lax",
    path: "/",
    secure: isSecure,
  });
}

export function clearTokens() {
  Cookies.remove("sso_access_token", { path: "/" });
  Cookies.remove("sso_refresh_token", { path: "/" });
  localStorage.removeItem("sso_user");
}

async function refreshAccessToken(): Promise<string | null> {
  const refresh = getRefreshToken();
  if (!refresh) return null;

  try {
    const res = await fetch(`${SSO_API_URL}/api/auth/refresh/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh }),
    });

    if (!res.ok) return null;

    const data = await res.json();
    // SimpleJWT with ROTATE_REFRESH_TOKENS returns new access + refresh
    setTokens(data.access, data.refresh || refresh);
    return data.access;
  } catch {
    return null;
  }
}

// Single-flight refresh — used by both the proactive ensureValidToken path
// and the reactive 401-retry path in authFetch. Concurrent callers share one
// in-flight refresh, preventing the rotation race where N parallel refreshes
// blacklist each other's tokens. See DEFECT-F2 / docs/auth-flow-debug/repros/F2_refresh_race.mjs.
async function singleFlightRefresh(): Promise<string | null> {
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }
  isRefreshing = true;
  refreshPromise = refreshAccessToken().finally(() => {
    isRefreshing = false;
    refreshPromise = null;
  });
  return refreshPromise;
}

async function ensureValidToken(): Promise<string | null> {
  const access = getAccessToken();
  if (access) return access;
  return singleFlightRefresh();
}

// ─── Core Fetch with Auth ────────────────────────────────────────────────────

async function authFetch<T>(
  path: string,
  options: RequestInit = {},
  retry = true
): Promise<T> {
  const token = await ensureValidToken();

  if (!token) {
    // No valid token at all — redirect to login
    clearTokens();
    if (typeof window !== "undefined") {
      window.location.href = "/login";
    }
    throw new ApiError(401, { error: "Not authenticated" });
  }

  const url = `${SSO_API_URL}${path}`;
  const isFormData = options.body instanceof FormData;
  // Attach frontend audit context so backend logs include page/component/action
  const auditPage =
    typeof window !== "undefined" ? window.location.pathname : "";
  const audit = consumeAuditContext();
  const res = await fetch(url, {
    ...options,
    headers: {
      ...(isFormData ? {} : { "Content-Type": "application/json" }),
      Authorization: `Bearer ${token}`,
      ...(auditPage ? { "X-Audit-Page": auditPage } : {}),
      ...(audit.component ? { "X-Audit-Component": audit.component } : {}),
      ...(audit.action ? { "X-Audit-Action": audit.action } : {}),
      ...options.headers,
    },
  });

  if (res.status === 401 && retry) {
    // Token expired — try refreshing once via the single-flight gate so
    // concurrent 401s share one refresh attempt (DEFECT-F2 fix).
    const newToken = await singleFlightRefresh();
    if (newToken) {
      return authFetch<T>(path, options, false);
    }
    // Refresh failed — redirect to login
    clearTokens();
    if (typeof window !== "undefined") {
      window.location.href = "/login";
    }
    throw new ApiError(401, { error: "Session expired" });
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(res.status, body);
  }

  // Handle 204 No Content
  if (res.status === 204) {
    return undefined as T;
  }

  return res.json();
}

// Public fetch (no auth needed)
async function publicFetch<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${SSO_API_URL}${path}`;
  const auditPage =
    typeof window !== "undefined" ? window.location.pathname : "";
  const audit = consumeAuditContext();
  const res = await fetch(url, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(auditPage ? { "X-Audit-Page": auditPage } : {}),
      ...(audit.component ? { "X-Audit-Component": audit.component } : {}),
      ...(audit.action ? { "X-Audit-Action": audit.action } : {}),
      ...options.headers,
    },
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(res.status, body);
  }

  return res.json();
}

// ─── API Methods ─────────────────────────────────────────────────────────────

export const ssoApi = {
  // ── Auth ──────────────────────────────────────────────────────────────────
  login(username: string, password: string): Promise<LoginResponse> {
    return publicFetch("/api/auth/login/", {
      method: "POST",
      body: JSON.stringify({ username, password }),
    });
  },

  logout(refresh?: string): Promise<void> {
    const refreshToken = refresh || getRefreshToken();

    // Always clear local tokens first
    clearTokens();

    if (!refreshToken) {
      return Promise.resolve();
    }

    // Per-device logout only — kills this browser's SSO session, leaves
    // sessions on phone, other laptops, etc. alive. The "Revoke other
    // sessions" affordance in security settings handles multi-device kills.
    return publicFetch<void>("/api/auth/logout/", {
      method: "POST",
      body: JSON.stringify({ refresh: refreshToken, logout_all: false }),
    }).catch(() => {
      // Ignore errors - local tokens are already cleared
    });
  },

  me(): Promise<MeResponse> {
    return authFetch("/api/auth/me/");
  },

  /**
   * Check current user without redirecting on failure.
   * Used by checkExistingAuth to silently verify if the session is valid.
   * Returns null if not authenticated (instead of redirecting to /login).
   */
  async silentMe(): Promise<MeResponse | null> {
    let token: string | null | undefined = getAccessToken();

    // If no access token, try refreshing
    if (!token) {
      token = await refreshAccessToken();
      if (!token) return null;
    }

    try {
      const url = `${SSO_API_URL}/api/auth/me/`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.ok) return res.json();

      // Token expired — try refreshing once
      if (res.status === 401) {
        const newToken = await refreshAccessToken();
        if (!newToken) return null;

        const retryRes = await fetch(url, {
          headers: { Authorization: `Bearer ${newToken}` },
        });

        if (retryRes.ok) return retryRes.json();
      }

      return null;
    } catch {
      return null;
    }
  },

  authorize(params: {
    client_id: string;
    redirect_uri: string;
  }): Promise<{ code: string; redirect_uri: string }> {
    const searchParams = new URLSearchParams(params);
    return authFetch(`/api/auth/authorize/?${searchParams.toString()}`);
  },

  /**
   * Try to authorize without redirecting to login on failure.
   * Returns null if authorization fails (token invalid, expired, etc).
   * Caller is responsible for redirecting to login with OAuth params.
   */
  async tryAuthorize(params: {
    client_id: string;
    redirect_uri: string;
  }): Promise<{ code: string; redirect_uri: string } | null> {
    try {
      // First ensure we have a valid token
      const token = await ensureValidToken();
      if (!token) return null;

      const searchParams = new URLSearchParams(params);
      const url = `${SSO_API_URL}/api/auth/authorize/?${searchParams.toString()}`;

      // Note: Don't add Content-Type for GET requests - it can trigger CORS preflight
      const res = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      // Codes that should propagate up as ApiError so the UI can handle
      // them explicitly (show a message or redirect to /apps/citizen).
      // Any other failure becomes `null` and is treated as "token is bad,
      // send to login". Keep this list in sync with SystemAccessResult in
      // accounts/services/permissions.py.
      const THROW_CODES = new Set([
        "no_system_access",        // legacy
        "no_role",
        "citizen_blocked",
        "position_type_missing",
        "account_inactive",
      ]);

      if (res.status === 401) {
        // Token might be expired, try refreshing once via the single-flight
        // gate so concurrent /authorize calls share one refresh attempt
        // (DEFECT-F2 fix — same gate used by authFetch).
        const newToken = await singleFlightRefresh();
        if (!newToken) return null;

        const retryRes = await fetch(url, {
          headers: {
            Authorization: `Bearer ${newToken}`,
          },
        });

        if (retryRes.status === 403) {
          const data = await retryRes.json().catch(() => ({}));
          if (THROW_CODES.has(data.error_code)) {
            throw new ApiError(403, data);
          }
        }

        if (!retryRes.ok) return null;
        return retryRes.json();
      }

      if (res.status === 403) {
        const data = await res.json().catch(() => ({}));
        if (THROW_CODES.has(data.error_code)) {
          throw new ApiError(403, data);
        }
      }

      if (!res.ok) return null;
      return res.json();
    } catch (err) {
      if (err instanceof ApiError) throw err;
      return null;
    }
  },

  getAuthorizeUrl(client_id: string, redirect_uri: string): string {
    const params = new URLSearchParams({ client_id, redirect_uri });
    return `${SSO_API_URL}/api/auth/authorize/?${params.toString()}`;
  },

  // ── Dashboard ─────────────────────────────────────────────────────────────
  dashboardStats(): Promise<DashboardStats> {
    return authFetch("/api/dashboard/stats/");
  },

  // Get accessible systems for current user
  accessibleSystems(): Promise<{ systems: AccessibleSystem[] }> {
    return authFetch("/api/auth/accessible-systems/");
  },

  // ── Users ─────────────────────────────────────────────────────────────────
  listUsers(params?: Record<string, string>): Promise<PaginatedResponse<UserList>> {
    const search = params ? `?${new URLSearchParams(params).toString()}` : "";
    return authFetch(`/api/sso/users/${search}`);
  },

  getUser(id: number): Promise<SSOUser> {
    return authFetch(`/api/sso/users/${id}/`);
  },

  createUser(data: Record<string, unknown>): Promise<SSOUser> {
    return authFetch("/api/sso/users/", {
      method: "POST",
      body: JSON.stringify(data),
    });
  },

  updateUser(id: number, data: Record<string, unknown>): Promise<SSOUser> {
    return authFetch(`/api/sso/users/${id}/`, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
  },

  deleteUser(id: number): Promise<void> {
    return authFetch(`/api/sso/users/${id}/`, { method: "DELETE" });
  },

  getUserPermissions(id: number): Promise<{ permissions: string[] }> {
    return authFetch(`/api/sso/users/${id}/permissions/`);
  },

  getUserRoles(id: number): Promise<{ roles: SSORole[] }> {
    return authFetch(`/api/sso/users/${id}/roles/`);
  },

  resetUser2FA(id: number): Promise<{ message: string; details: Record<string, unknown> }> {
    return authFetch(`/api/sso/users/${id}/reset-2fa/`, { method: "POST" });
  },

  getUserSessions(userId: number): Promise<UserSession[]> {
    return authFetch(`/api/sso/users/${userId}/sessions/`);
  },

  getUserLoginHistory(userId: number, limit?: number): Promise<LoginAttempt[]> {
    const query = limit ? `?limit=${limit}` : "";
    return authFetch(`/api/sso/users/${userId}/login-history/${query}`);
  },

  checkUsername(username: string, excludeId?: number): Promise<{ available: boolean; username: string }> {
    return authFetch("/api/sso/users/check-username/", {
      method: "POST",
      body: JSON.stringify({ username, exclude_id: excludeId }),
    });
  },

  checkNationalId(nationalId: string, excludeId?: number): Promise<{ available: boolean; national_id: string }> {
    return authFetch("/api/sso/users/check-national-id/", {
      method: "POST",
      body: JSON.stringify({ national_id: nationalId, exclude_id: excludeId }),
    });
  },

  // ── Departments ───────────────────────────────────────────────────────────
  listDepartments(params?: Record<string, string>): Promise<PaginatedResponse<SSODepartment>> {
    const search = params ? `?${new URLSearchParams(params).toString()}` : "";
    return authFetch(`/api/sso/departments/${search}`);
  },

  getDepartment(id: number): Promise<SSODepartment> {
    return authFetch(`/api/sso/departments/${id}/`);
  },

  createDepartment(data: Record<string, unknown>): Promise<SSODepartment> {
    return authFetch("/api/sso/departments/", {
      method: "POST",
      body: JSON.stringify(data),
    });
  },

  updateDepartment(id: number, data: Record<string, unknown>): Promise<SSODepartment> {
    return authFetch(`/api/sso/departments/${id}/`, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
  },

  deleteDepartment(id: number): Promise<void> {
    return authFetch(`/api/sso/departments/${id}/`, { method: "DELETE" });
  },

  departmentTree(): Promise<SSODepartmentTreeNode[]> {
    return authFetch("/api/sso/departments/tree/");
  },

  // ── Roles ─────────────────────────────────────────────────────────────────
  listRoles(params?: Record<string, string>): Promise<PaginatedResponse<SSORole>> {
    const search = params ? `?${new URLSearchParams(params).toString()}` : "";
    return authFetch(`/api/sso/roles/${search}`);
  },

  getRole(id: number): Promise<SSORole> {
    return authFetch(`/api/sso/roles/${id}/`);
  },

  createRole(data: Record<string, unknown>): Promise<SSORole> {
    return authFetch("/api/sso/roles/", {
      method: "POST",
      body: JSON.stringify(data),
    });
  },

  updateRole(id: number, data: Record<string, unknown>): Promise<SSORole> {
    return authFetch(`/api/sso/roles/${id}/`, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
  },

  deleteRole(id: number): Promise<void> {
    return authFetch(`/api/sso/roles/${id}/`, { method: "DELETE" });
  },

  // ── Permissions ───────────────────────────────────────────────────────────
  listPermissions(params?: Record<string, string>): Promise<PaginatedResponse<SSOPermission>> {
    const search = params ? `?${new URLSearchParams(params).toString()}` : "";
    return authFetch(`/api/sso/permissions/${search}`);
  },

  getPermission(id: number): Promise<SSOPermissionDetail> {
    return authFetch(`/api/sso/permissions/${id}/`);
  },

  createPermission(data: Record<string, unknown>): Promise<SSOPermission> {
    return authFetch("/api/sso/permissions/", {
      method: "POST",
      body: JSON.stringify(data),
    });
  },

  updatePermission(id: number, data: Record<string, unknown>): Promise<SSOPermission> {
    return authFetch(`/api/sso/permissions/${id}/`, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
  },

  deletePermission(id: number): Promise<void> {
    return authFetch(`/api/sso/permissions/${id}/`, { method: "DELETE" });
  },

  // ── OAuth Clients ─────────────────────────────────────────────────────────
  listOAuthClients(params?: Record<string, string>): Promise<PaginatedResponse<OAuthClient>> {
    const search = params ? `?${new URLSearchParams(params).toString()}` : "";
    return authFetch(`/api/sso/oauth-clients/${search}`);
  },

  /** Bulk import permissions for one OAuth client from a JSON payload.
   * Each item upserts on ``(name, system)``. Returns a per-row report. */
  bulkImportPermissions(
    payload: {
      client_id?: string;
      system?: number;
      permissions: Array<{
        name: string;
        module: string;
        display_name?: string;
        name_ar?: string;
        module_ar?: string;
        description?: string;
      }>;
    },
  ): Promise<{
    client_id: string;
    system_id: number;
    system_name?: string;
    system_name_en?: string;
    created: number;
    updated: number;
    errors: Array<{ index?: number; name?: string; error: string }>;
  }> {
    return authFetch("/api/sso/permissions/bulk-import/", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },

  getOAuthClient(id: number): Promise<OAuthClient> {
    return authFetch(`/api/sso/oauth-clients/${id}/`);
  },

  createOAuthClient(data: Record<string, unknown>): Promise<OAuthClient> {
    return authFetch("/api/sso/oauth-clients/", {
      method: "POST",
      body: JSON.stringify(data),
    });
  },

  updateOAuthClient(id: number, data: Record<string, unknown>): Promise<OAuthClient> {
    return authFetch(`/api/sso/oauth-clients/${id}/`, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
  },

  deleteOAuthClient(id: number): Promise<void> {
    return authFetch(`/api/sso/oauth-clients/${id}/`, { method: "DELETE" });
  },

  rotateOAuthSecret(id: number): Promise<OAuthClient> {
    return authFetch(`/api/sso/oauth-clients/${id}/rotate-secret/`, {
      method: "POST",
    });
  },

  // ── Session Management ───────────────────────────────────────────────────
  listSessions(): Promise<UserSession[]> {
    return authFetch("/api/auth/sessions/");
  },

  revokeSession(sessionId: number): Promise<{ detail: string }> {
    return authFetch("/api/auth/sessions/revoke/", {
      method: "POST",
      body: JSON.stringify({ session_id: sessionId }),
    });
  },

  revokeOtherSessions(): Promise<{ detail: string; count: number }> {
    return authFetch("/api/auth/sessions/revoke-others/", {
      method: "POST",
    });
  },

  getLoginHistory(params?: {
    limit?: number;
    include_failed?: boolean;
  }): Promise<LoginAttempt[]> {
    const searchParams = new URLSearchParams();
    if (params?.limit) searchParams.set("limit", String(params.limit));
    if (params?.include_failed !== undefined)
      searchParams.set("include_failed", String(params.include_failed));
    const query = searchParams.toString();
    return authFetch(`/api/auth/login-history/${query ? `?${query}` : ""}`);
  },

  // ── Two-Factor Authentication ────────────────────────────────────────────
  get2FAStatus(): Promise<TwoFactorStatus> {
    return authFetch("/api/auth/2fa/status/");
  },

  enableEmailOTP(): Promise<{ detail: string; status: TwoFactorStatus }> {
    return authFetch("/api/auth/2fa/enable/email/", {
      method: "POST",
    });
  },

  setupTOTP(): Promise<TOTPSetupResponse> {
    return authFetch("/api/auth/2fa/enable/totp/", {
      method: "POST",
    });
  },

  verifyTOTPSetup(code: string): Promise<{ detail: string; status: TwoFactorStatus }> {
    return authFetch("/api/auth/2fa/verify/totp/", {
      method: "POST",
      body: JSON.stringify({ code }),
    });
  },

  disableTOTP(): Promise<{ detail: string; status: TwoFactorStatus }> {
    return authFetch("/api/auth/2fa/disable/totp/", {
      method: "POST",
    });
  },

  disable2FA(password: string): Promise<{ detail: string }> {
    return authFetch("/api/auth/2fa/disable/", {
      method: "POST",
      body: JSON.stringify({ password }),
    });
  },

  getBackupCodesInfo(): Promise<BackupCodesInfo> {
    return authFetch("/api/auth/2fa/backup-codes/");
  },

  generateBackupCodes(): Promise<BackupCodesResponse> {
    return authFetch("/api/auth/2fa/backup-codes/", {
      method: "POST",
    });
  },

  // ── Trusted Devices ──────────────────────────────────────────────────────
  listTrustedDevices(): Promise<TrustedDevice[]> {
    return authFetch("/api/auth/trusted-devices/");
  },

  revokeTrustedDevice(deviceId: number): Promise<void> {
    return authFetch(`/api/auth/trusted-devices/${deviceId}/`, {
      method: "DELETE",
    });
  },

  revokeAllTrustedDevices(): Promise<{ detail: string; count: number }> {
    return authFetch("/api/auth/trusted-devices/revoke-all/", {
      method: "POST",
    });
  },

  // ── UAE Pass ────────────────────────────────────────────────────────────
  uaePassAuthorize(params?: {
    client_id?: string;
    redirect_uri?: string;
  }): Promise<UAEPassAuthorizeResponse> {
    const searchParams = new URLSearchParams();
    if (params?.client_id) searchParams.set("client_id", params.client_id);
    if (params?.redirect_uri) searchParams.set("redirect_uri", params.redirect_uri);
    const query = searchParams.toString();
    return publicFetch(`/api/auth/uaepass/authorize/${query ? `?${query}` : ""}`);
  },

  uaePassCallback(data: {
    code: string;
    state: string;
  }): Promise<UAEPassCallbackResponse> {
    return publicFetch("/api/auth/uaepass/callback/", {
      method: "POST",
      body: JSON.stringify(data),
    });
  },

  uaePassLogout(redirectUrl?: string): Promise<{ logout_url: string }> {
    const params = redirectUrl ? `?redirect_url=${encodeURIComponent(redirectUrl)}` : "";
    return authFetch(`/api/auth/uaepass/logout/${params}`);
  },

  setInitialPassword(data: {
    password: string;
    confirm_password: string;
  }): Promise<{ detail: string; access?: string; refresh?: string }> {
    return authFetch("/api/auth/set-initial-password/", {
      method: "POST",
      body: JSON.stringify(data),
    });
  },

  changePassword(data: {
    current_password: string;
    new_password: string;
    confirm_password: string;
  }): Promise<{ success: boolean; message: string }> {
    return authFetch("/api/sso/users/change_password/", {
      method: "POST",
      body: JSON.stringify(data),
    });
  },

  // ── Forgot Password ──────────────────────────────────────────────────────
  forgotPasswordSendOTP(email: string): Promise<{
    detail: string;
    email_hint: string;
    expires_in: number;
    resends_remaining: number;
  }> {
    return publicFetch("/api/auth/forgot-password/send-otp/", {
      method: "POST",
      body: JSON.stringify({ email }),
    });
  },

  forgotPasswordVerifyOTP(
    email: string,
    code: string
  ): Promise<{ detail: string; reset_token: string }> {
    return publicFetch("/api/auth/forgot-password/verify-otp/", {
      method: "POST",
      body: JSON.stringify({ email, code }),
    });
  },

  forgotPasswordReset(
    reset_token: string,
    password: string,
    confirm_password: string
  ): Promise<{ detail: string }> {
    return publicFetch("/api/auth/forgot-password/reset/", {
      method: "POST",
      body: JSON.stringify({ reset_token, password, confirm_password }),
    });
  },

  // ── 2FA Login Flow ───────────────────────────────────────────────────────
  verify2FA(data: {
    session_token: string;
    code: string;
    method?: "email_otp" | "totp" | "backup_code";
    trust_device?: boolean;
  }): Promise<LoginResponse> {
    return publicFetch("/api/auth/login/verify-2fa/", {
      method: "POST",
      body: JSON.stringify(data),
    });
  },

  resend2FAOTP(sessionToken: string): Promise<{
    detail: string;
    resends_remaining: number;
  }> {
    return publicFetch("/api/auth/login/resend-otp/", {
      method: "POST",
      body: JSON.stringify({ session_token: sessionToken }),
    });
  },

  // ── RBAC: User ↔ Department membership (DESIGN §5.2) ───────────────────
  listUserDepartments(
    userId: number,
  ): Promise<{ departments: UserDepartmentMembership[] }> {
    return authFetch(`/api/sso/users/${userId}/departments/`);
  },

  addUserDepartment(
    userId: number,
    data: { department_id: number; is_primary?: boolean },
  ): Promise<UserDepartmentMembership> {
    return authFetch(`/api/sso/users/${userId}/departments/`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  },

  updateUserDepartment(
    userId: number,
    deptId: number,
    data: { is_primary: true },
  ): Promise<UserDepartmentMembership> {
    return authFetch(`/api/sso/users/${userId}/departments/${deptId}/`, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
  },

  /** Triggers the scope-7 cascade; response is the CascadeResult. */
  removeUserDepartment(
    userId: number,
    deptId: number,
  ): Promise<CascadeResult> {
    return authFetch(`/api/sso/users/${userId}/departments/${deptId}/`, {
      method: "DELETE",
    });
  },

  // ── RBAC: User role assignment (DESIGN §5.1) ─────────────────────────
  assignUserRole(
    userId: number,
    data: AssignUserRoleRequest,
  ): Promise<SSOUserRole> {
    return authFetch(`/api/sso/users/${userId}/roles/`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  },

  revokeUserRole(userId: number, userRoleId: number): Promise<void> {
    return authFetch(`/api/sso/users/${userId}/roles/${userRoleId}/`, {
      method: "DELETE",
    });
  },

  listAssignableDepartments(
    userId: number,
  ): Promise<{ departments: AssignableDepartment[] }> {
    return authFetch(`/api/sso/users/${userId}/assignable-departments/`);
  },

  listAssignableRoles(
    deptId: number,
  ): Promise<{ roles: AssignableRole[] }> {
    return authFetch(`/api/sso/departments/${deptId}/assignable-roles/`);
  },

  // ── RBAC: Delegations (DESIGN §5.3) ──────────────────────────────────
  listDelegations(
    filter?: DelegationListFilter,
  ): Promise<{ results: DelegationDetail[] }> {
    const search = filter?.role ? `?role=${encodeURIComponent(filter.role)}` : "";
    return authFetch(`/api/sso/delegations/${search}`);
  },

  createDelegation(
    data: CreateDelegationRequest,
  ): Promise<DelegationDetail> {
    return authFetch("/api/sso/delegations/", {
      method: "POST",
      body: JSON.stringify(data),
    });
  },

  getDelegation(id: number): Promise<DelegationDetail> {
    return authFetch(`/api/sso/delegations/${id}/`);
  },

  listPendingDelegations(
    params?: Record<string, string>,
  ): Promise<PaginatedResponse<DelegationDetail>> {
    const search = params ? `?${new URLSearchParams(params).toString()}` : "";
    return authFetch(`/api/sso/delegations/pending/${search}`);
  },

  approveDelegation(id: number): Promise<DelegationDetail> {
    return authFetch(`/api/sso/delegations/${id}/approve/`, { method: "POST" });
  },

  rejectDelegation(id: number, data: { reason: string }): Promise<DelegationDetail> {
    return authFetch(`/api/sso/delegations/${id}/reject/`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  },

  revokeDelegation(id: number): Promise<DelegationDetail> {
    return authFetch(`/api/sso/delegations/${id}/revoke/`, { method: "POST" });
  },

  // ── RBAC: Archive browse + restore (DESIGN §5.4, superuser-only) ─────
  listArchives(
    kind: "user-departments" | "user-roles",
    params?: Record<string, string>,
  ): Promise<PaginatedResponse<ArchiveRow>> {
    const search = params ? `?${new URLSearchParams(params).toString()}` : "";
    return authFetch(`/api/sso/archives/${kind}/${search}`);
  },

  restoreArchive(
    kind: "user-departments" | "user-roles",
    archiveId: number,
  ): Promise<RestoreResult> {
    return authFetch(`/api/sso/archives/${kind}/${archiveId}/restore/`, {
      method: "POST",
    });
  },
};

// ─── RBAC Refactor Types (DESIGN §5) ────────────────────────────────────────

export interface UserDepartmentMembership {
  id: number;
  user_id: number;
  department_id: number;
  department_code: string;
  department_name: string;
  is_primary: boolean;
  joined_at: string;
}

export interface CascadeResult {
  cascade_event_id: string;
  archived_user_roles: number;
  archived_user_departments: number;
  revoked_delegations: number;
  revoked_sessions: number;
  new_primary_department_id: number | null;
}

export interface AssignUserRoleRequest {
  role_id: number;
  department_id: number;
  valid_from?: string;
  valid_until?: string;
}

export interface AssignableDepartment {
  id: number;
  code: string;
  name: string;
  is_primary_for_user: boolean;
}

export interface AssignableRole {
  id: number;
  code: string;
  name: string;
  permission_count: number;
}

export type DelegationStatus =
  | "pending"
  | "active"
  | "rejected"
  | "ended"
  | "revoked";

export type DelegationScope = "all" | "selected_roles" | "selected_perms";

export interface DelegationDetail {
  id: number;
  delegator_id: number;
  delegator_name: string;
  delegatee_id: number;
  delegatee_name: string;
  filed_by_id: number;
  source_department_id: number | null;
  scope: DelegationScope;
  exclusive: boolean;
  valid_from: string;
  valid_until: string;
  reason: string;
  status: DelegationStatus;
  approved_by_id: number | null;
  approved_at: string | null;
  rejection_reason: string;
  created_at: string;
}

export interface DelegationListFilter {
  role?: "delegator" | "delegatee" | "filed";
}

export interface CreateDelegationRequest {
  /** Required unless admin-on-behalf (then set delegator_user_id). */
  delegatee_id: number;
  valid_from: string;
  valid_until: string;
  reason: string;
  exclusive: boolean;
  /** Superuser-only: file on behalf of another user. */
  delegator_user_id?: number;
  /** Defaults to "all" server-side. */
  scope?: DelegationScope;
  /** Required when scope="selected_roles". */
  role_ids?: number[];
  /** Required when scope="selected_perms". */
  permission_ids?: number[];
  source_department_id?: number;
}

export interface ArchiveRow {
  id: number;
  user_id: number;
  department_id: number;
  role_id?: number;
  was_primary?: boolean;
  joined_at?: string;
  valid_from?: string | null;
  valid_until?: string | null;
  assigned_by_id?: number | null;
  original_row_id: number;
  deleted_at: string;
  deleted_by_id: number | null;
  cascade_event_id: string;
}

export interface RestoreResult {
  id: number;
  user_id: number;
  department_id: number;
  role_id?: number;
  is_primary?: boolean;
  joined_at?: string;
  is_active?: boolean;
}

// ─── Session & 2FA Types ────────────────────────────────────────────────────

export interface UserSession {
  id: number;
  device_name: string;
  device_type: "desktop" | "mobile" | "tablet" | "unknown";
  browser: string;
  os: string;
  ip_address: string | null;
  city: string;
  country: string;
  country_code: string;
  last_activity: string;
  created_at: string;
  is_current: boolean;
  is_revoked: boolean;
  revoked_at: string | null;
  client_name: string;
  time_ago: string;
  session_duration: string;
}

export interface LoginAttempt {
  id: number;
  username_attempted: string;
  success: boolean;
  failure_reason: string;
  used_2fa: boolean;
  used_2fa_method: string;
  ip_address: string | null;
  city: string;
  country: string;
  country_code: string;
  created_at: string;
  status: "success" | "failed";
  status_display: string;
  time_ago: string;
}

export interface TwoFactorStatus {
  is_enabled: boolean;
  email_otp_enabled: boolean;
  totp_enabled: boolean;
  totp_confirmed: boolean;
  totp_created_at: string | null;
  preferred_method: "email_otp" | "totp";
  available_methods: string[];
  has_any_method: boolean;
  active_method: string;
  backup_codes_remaining: number;
  backup_codes_generated_at: string | null;
  email_hint: string;
}

export interface TOTPSetupResponse {
  secret: string;
  provisioning_uri: string;
  qr_code: string;
}

export interface BackupCodesInfo {
  remaining: number;
  generated_at: string | null;
  total: number;
}

export interface BackupCodesResponse {
  codes: string[];
  count: number;
  message: string;
}

export interface TrustedDevice {
  id: number;
  device_name: string;
  ip_address: string | null;
  city: string;
  country: string;
  trusted_until: string;
  created_at: string;
  is_valid: boolean;
  is_revoked: boolean;
  days_remaining: number;
}

export interface TwoFactorRequiredResponse {
  requires_2fa: true;
  session_token: string;
  methods: string[];
  email_hint: string;
  preferred_method: string;
  expires_in: number;
}

// ─── UAE Pass Types ──────────────────────────────────────────────────────────

export interface UAEPassAuthorizeResponse {
  authorization_url: string;
  state: string;
}

export interface UAEPassCallbackResponse {
  access: string;
  refresh: string;
  access_lifetime: number;
  user: UserProfile;
  permissions: string[];
  roles: SSOUserRole[];
  is_new_link: boolean;
  is_new_citizen: boolean;
  requires_password_setup: boolean;
  totp_setup_required?: boolean;
  // Present when UAE Pass login was part of OAuth flow
  oauth_code?: string;
  oauth_redirect_uri?: string;
  // Present when user has no roles for the target system
  system_access_denied?: boolean;
}

// ─── System Email Types ──────────────────────────────────────────────────────

export interface SystemEmailConfig {
  is_configured: boolean;
  email_method: string;
  email_host: string;
  email_port: number;
  default_from_email: string;
  from_name: string;
  last_connection_test: string | null;
  connection_error: string;
}

// ─── System Email API Methods ────────────────────────────────────────────────

export const systemEmailApi = {
  // Get system email configuration status
  getConfig(): Promise<SystemEmailConfig> {
    return authFetch("/api/system-email/config/");
  },

  // Update system email configuration (from_name)
  updateConfig(data: { from_name?: string }): Promise<{ success: boolean; from_name: string }> {
    return authFetch("/api/system-email/config/", {
      method: "PATCH",
      body: JSON.stringify(data),
    });
  },

  // Send test email
  sendTestEmail(testEmail: string): Promise<{ success: boolean; message: string }> {
    return authFetch("/api/system-email/test/", {
      method: "POST",
      body: JSON.stringify({ test_email: testEmail }),
    });
  },
};


